import { stripHTML } from "@decocms/apps-website/utils/html"
import ProductCard, {
    LoadingFallback as ProductCardLoadingFallback,
} from "~/components/product/ProductCard"
import Filters from "~/components/search/Filters"
import { portugueseMappings } from "~/components/search/Sort"
import Breadcrumb from "~/components/ui/Breadcrumb"
import Icon from "~/components/ui/Icon"
import Pagination, { MAX_FROM_PARAM_VTEX, VTEX_MAX_PAGE } from "~/components/ui/Pagination"
import URLtoBreadcrumb from "~/sdk/URLtoBreadcrumb"
import clx from "~/sdk/clx"
import { isSchedulingActive, Scheduling } from "~/sdk/contentScheduling"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import { toProductItem } from "~/sdk/ga4/transform/toProductItem"
import type { SectionRedirect } from "~/sections/Scripts/RedirectDepartments"
import type {
    BreadcrumbList,
    FilterToggleValue,
    ProductListingPage,
    ViewItemListParams,
} from "@decocms/apps-commerce/types"

import { LegacySort } from "@decocms/apps-vtex/utils/types"

import { useMemo } from "react"
// `fetchLegacyPlpCached` NÃO entra como import estático: este arquivo é um
// componente de section, logo está no grafo do bundle do CLIENTE, e
// `~/setup/commerce-loaders` arrasta `@decocms/apps-vtex` inteiro (302 KB) +
// o próprio módulo (38 KB) junto. Medido por sourcemap: era um dos caminhos
// que inflavam o chunk `vendor-router` do cliente para 1,3 MB.
//
// A função só é usada dentro do `loader` abaixo, que roda exclusivamente no
// servidor — então o import dinâmico vira um chunk à parte que o browser
// nunca baixa. Ver o cabeçalho de `~/setup.ts`.
const loadPlpFetcher = () =>
  import("~/setup/commerce-loaders").then((m) => m.fetchLegacyPlpCached)
import { capitalize } from "~/sdk/utils/capitalize"
import { SendEventOnView } from "~/components/Analytics"
import SendCustomEventOnView from "~/components/analytics/SendCustomEventOnView"
import FiltersDrawer from "~/components/search/FiltersDrawer"
import DepartmentCategoryNav, {
    matchCategoryNav,
    type CategoryNavGroup,
} from "~/components/category/DepartmentCategoryNav"
import breadcrumbOrCurrent from "~/sdk/breadcrumbOrCurrent"
import { useId } from "react"
import { checkDisabledBuyMonthlySubscription, MONTHLY_SKU_ID } from "~/sdk/useSubscription"
import stripTrackingParams from "~/sdk/stripTrackingParams"
import { trimProductForShelf } from "~/sdk/trimProductForShelf"
import { SITE } from "~/constants"

/**
 * @titleBy label
 */
export interface Page {
    /**
     * @title Texto mostrado na lista anterior
     */
    label: string
    page: ProductListingPage | null
    scheduling: Scheduling
}
export interface Props {
    /** @title Integration */
    pages: Page[]
    /**
     * @title Parâmetros da GA4
     */
    ga4?: ViewItemListParams
    /**
     * @title Paginar filtros no mobile
     */
    paginateFilterOnMobile?: boolean
    /**
     * @title Ordernação da pagina de marca
     */
    brandSort?: LegacySort
    /**
     * @ignore
     */
    brandPage?: boolean
    /**
     * @title Navegue por Categorias
     * @description Thumbs por departamento (ONG #21228). Use matcher de URL para liberar só nas categorias desejadas.
     */
    categoryNav?: CategoryNavGroup[]
}

// Mapping from legacy sort names to modern sort values
const legacyToModernSort: Record<string, string> = {
    'OrderByPriceDESC': 'price:desc',
    'OrderByPriceASC': 'price:asc',
    'OrderByTopSaleDESC': 'orders:desc',
    'OrderByReleaseDateDESC': 'release:desc',
    'OrderByBestDiscountDESC': 'discount:desc',
    'OrderByScoreDESC': 'relevance:desc',
    'OrderByNameASC': 'name:asc',
    'OrderByNameDESC': 'name:desc',
}

const modernToLegacySort: Record<string, string> = Object.fromEntries(
    Object.entries(legacyToModernSort).map(([legacy, modern]) => [modern, legacy]),
)

// `page` may be the original ProductListingPage or a derived shape where
// filter `values` have been remapped (still ProductListingPage-compatible at
// runtime; the TS shape only widens because of the spread/map rebuild).
type LoaderPage = Omit<ProductListingPage, "filters"> & { filters: ProductListingPage["filters"] | unknown[] }

export interface LoaderOutput {
    page: LoaderPage | null
    paginateFilterOnMobile?: boolean
    isMobile: boolean
    url: string
    isSearchPage: boolean
    ga4?: ViewItemListParams
    isPerformanceTestPage: boolean
    brandPage?: boolean
    disabledBuyMonthlySubscription?: boolean
    categoryNav?: CategoryNavGroup[]
    /** Consumed by the catch-all route loader — see `withRedirect` below. */
    __redirect?: SectionRedirect
}

export async function loader(
    props: Props & { __pageUrl?: string; isMobile?: boolean },
): Promise<LoaderOutput> {
    const pageUrl = props.__pageUrl ?? "/"
    const url = new URL(pageUrl, SITE.URL)
    const sortFromUrl = url.searchParams.get('O') as LegacySort | null

    let page = props.pages.find(({ scheduling }) => isSchedulingActive(scheduling))
        ?.page ?? null

    // Trim an IMMUTABLE copy for the grid — never mutate `page` in place: the
    // same resolved ProductListingPage object is memo-shared with PageSEO's
    // JSON-LD, which bots still need with full variants. Cuts the ~14 MB
    // dehydration payload (full N² variants → lean) without touching the card.
    if (page) page = { ...page, products: page.products.map(trimProductForShelf) }

    // `pages`/`csv`/`items` are loader-only inputs — `pages[].page` is the raw
    // ProductListingPage with FULL variants (~13 MB). The component reads only
    // the returned `page`, so exclude these from every serialized output below.
    const restProps = { ...props } as Record<string, unknown>
    delete restProps.pages
    delete restProps.csv
    // Os grupos de TODOS os departamentos viriam serializados no payload de
    // toda PLP (no Fresh o SearchResult não era island); só o que casa segue.
    const matchedNav = matchCategoryNav(props.categoryNav, pageUrl)
    restProps.categoryNav = matchedNav ? [matchedNav] : undefined
    delete restProps.items

    // Busca case-insensitive: `?q=Hot Flowers` redireciona 301 para `?q=hot
    // flowers`, para não fragmentar cache/canonical entre variações de caixa
    // (upstream `dba2c1249`). Como todo redirect de section aqui, sai pelo
    // marcador `__redirect` — ver `withRedirect` abaixo.
    const searchTerm = url.searchParams.get('q')
    if (searchTerm && searchTerm !== searchTerm.toLowerCase()) {
        const lowered = new URL(url.href)
        lowered.searchParams.set('q', searchTerm.toLowerCase())
        return {
            ...restProps,
            page,
            isMobile: props.isMobile ?? false,
            url: pageUrl,
            isSearchPage: url.pathname === '/s',
            isPerformanceTestPage: url.pathname.includes('performance'),
            __redirect: { href: `${lowered.pathname}${lowered.search}`, status: 301 },
        }
    }

    if (sortFromUrl && page) {
        // Convert legacy sort format to modern format if needed
        const modernSort = legacyToModernSort[sortFromUrl as string] || sortFromUrl
        const hasSortOption = page.sortOptions?.some((option) => option.label === modernSort)
        if (!hasSortOption) {
            return {
                ...restProps,
                page,
                isMobile: props.isMobile ?? false,
                url: pageUrl,
                isSearchPage: url.pathname === '/s',
                isPerformanceTestPage: url.pathname.includes('performance'),
            }
        }

        const sortedPageHref = new URL(pageUrl, SITE.URL)
        sortedPageHref.searchParams.set('PS', '36')

        // The legacy Catalog API only understands "OrderBy*" names and reads
        // `O=` straight from the URL — a modern value there ("orders:desc")
        // returns an empty result set. A value we can't translate would empty
        // the page (→ redirect /buscavazia), so fall back to default ordering.
        const legacySort = (modernToLegacySort[modernSort] ?? sortFromUrl) as LegacySort
        // OrderByTopSaleDESC is the store's default sort — the CMS page was
        // already loaded with it via COMMERCE_LOADERS. Explicitly requesting it
        // in a second Catalog API call returns 0 results for some category paths.
        if (legacySort.startsWith('OrderBy') && legacySort !== 'OrderByTopSaleDESC') {
            sortedPageHref.searchParams.set('O', legacySort)

            // Pela instância CACHEADA do grid (mesma chave L1/L2): a chamada
            // direta ao app aqui era VTEX crua em toda request com ?O=,
            // invisível no painel — e dobrada, porque o loader de CMS já rodou.
            const sortedPage = await (await loadPlpFetcher())({
                __pageUrl: sortedPageHref.href,
                sort: legacySort,
            })
            if (sortedPage) {
                page = { ...sortedPage, products: sortedPage.products.map(trimProductForShelf) }
            }
        }
    }

    const disabledBuyMonthlySubscription = await checkDisabledBuyMonthlySubscription(
        page?.products ?? [],
    )

    // Section loaders can't throw a router redirect: `runSectionLoaders`
    // swallows it and the whole section then renders nothing — a search for a
    // brand name ("hot flowers") ended up as a blank page between header and
    // footer instead of the 301 production serves. Emit the `__redirect`
    // marker instead; src/routes/$.tsx turns it into a real redirect (same
    // pattern as Scripts/RedirectDepartments, DECO-5821).
    const withRedirect = (href: string, status = 301): LoaderOutput => ({
        ...restProps,
        page,
        isMobile: props.isMobile ?? false,
        url: pageUrl,
        isSearchPage: url.pathname === '/s',
        isPerformanceTestPage: url.pathname.includes('performance'),
        disabledBuyMonthlySubscription,
        __redirect: { href, status },
    })

    if (!page?.products.length) {
        const term = `${url.pathname}${url.search}`
        return withRedirect(`/buscavazia?t=${term ?? 'empty-search'}`)
    }

    if ((page?.pageInfo.currentPage ?? 1) > VTEX_MAX_PAGE) {
        return withRedirect(`${url.pathname}?page=50`)
    }

    /* --------- Redirect to brand page --------- */
    const brands = page?.filters
        .find((filter) => filter.key === 'Brands')
        ?.values as FilterToggleValue[]
    if (brands?.length) {
        const term = decodeURI(url.searchParams.get('q') ?? '')
        const brand = brands.find((brand) => brand.label.toLowerCase() === term.toLowerCase())
        if (brand) {
            const brandUrl = new URL(brand.url, url.href)
            brandUrl.searchParams.delete('q')
            brandUrl.pathname = brandUrl.pathname.toLowerCase()
            return withRedirect(`${brandUrl.pathname}${brandUrl.search}`)
        }
    }
    /* --------- Redirect to brand page --------- */

    const isSearchPage = url.pathname === '/s'

    const newUrl = new URL(pageUrl, SITE.URL)
    const mapParam = newUrl.searchParams.get('map')

    if (mapParam) {
        return {
            ...restProps,
            page,
            isMobile: props.isMobile ?? false,
            url: pageUrl,
            isSearchPage,
            isPerformanceTestPage: new URL(pageUrl, SITE.URL).pathname.includes('performance'),
            disabledBuyMonthlySubscription,
        }
    }

    const filterBrandPage = page?.filters.find((f) => {
        return f.key === 'Brands'
    })

    if (!filterBrandPage) {
        return {
            ...restProps,
            page,
            isMobile: props.isMobile ?? false,
            url: pageUrl,
            isSearchPage,
            isPerformanceTestPage: new URL(pageUrl, SITE.URL).pathname.includes('performance'),
            disabledBuyMonthlySubscription,
        }
    }

    const filterBrandPageValues = Array.isArray(filterBrandPage.values) ? filterBrandPage.values : []
    const valuesFilterBrandPage = filterBrandPageValues.map((v) => {
        const url = v.url
        const newlinkSplit = url.split('?map=')
        const newlink = newlinkSplit[0]
        return {
            ...v,
            url: newlink,
        }
    })

    const newBrand = {
        ...filterBrandPage,
        values: valuesFilterBrandPage,
    }

    const newFilters = page?.filters.map((f) => {
        if (f.key === 'Brands') return newBrand
        return f
    })

    const newPageReplaceUrlBrand = {
        ...page,
        filters: newFilters,
    }

    const filterCategory = page?.filters.find((f) => {
        return f.key === 'Categories'
    })

    if (!filterCategory) {
        return {
            ...restProps,
            page: newPageReplaceUrlBrand,
            isMobile: props.isMobile ?? false,
            url: pageUrl,
            isSearchPage,
            isPerformanceTestPage: new URL(pageUrl, SITE.URL).pathname.includes('performance'),
            brandPage: true,
            disabledBuyMonthlySubscription,
        }
    }

    const filterCategoryValuesOuter = Array.isArray(filterCategory.values) ? filterCategory.values : []
    const valuesFilterCategoryPage = filterCategoryValuesOuter.map((v) => {
        const url = v.url
        const newlinkSplit = url.split('?map=')
        const newlink = newlinkSplit[0]
        return {
            ...v,
            url: newlink,
        }
    })

    const newCategory = {
        ...filterCategory,
        values: valuesFilterCategoryPage,
    }

    const newFiltersCategorys = page?.filters.map((f) => {
        if (f.key === 'Categories') return newCategory
        if (f.key === 'Brands') return newBrand
        return f
    })

    const newPageReplaceUrl = {
        ...page,
        filters: newFiltersCategorys,
    }

    return {
        ...restProps,
        page: newPageReplaceUrl,
        isMobile: props.isMobile ?? false,
        url: pageUrl,
        isSearchPage,
        isPerformanceTestPage: new URL(pageUrl, SITE.URL).pathname.includes('performance'),
        brandPage: true,
        disabledBuyMonthlySubscription,
    }
}

export default function SearchResult(
    {
        page,
        paginateFilterOnMobile,
        isMobile,
        url,
        isSearchPage,
        ga4,
        isPerformanceTestPage,
        brandPage,
        disabledBuyMonthlySubscription,
        categoryNav,
    }: Awaited<ReturnType<typeof loader>>,
) {
    if (!page) return null
    const productListId = useId()
    const { products, breadcrumb, pageInfo, sortOptions } = page
    // The loader may rebuild filters via map/spread, which widens the inferred
    // element type to `unknown`; cast back to the canonical Filter[] for UI.
    const filters = page.filters as ProductListingPage["filters"]
    const matchedCategoryNav = matchCategoryNav(categoryNav, url ?? "/")

    // Não repassar tracking/PII (utm_*, gclid, ?email=…) nos links de
    // ordenação derivados desta URL (DECO-5819).
    const URLi = stripTrackingParams(new URL(url ?? '/', SITE.URL))
    let breadcrumbItems: BreadcrumbList['itemListElement'] = isSearchPage
        ? breadcrumb.itemListElement
        : URLtoBreadcrumb(url)
    if (!isSearchPage) {
        for (
            const [i, name] of breadcrumbOrCurrent(
                breadcrumb.itemListElement,
                breadcrumbItems.map((b) => b.name ?? ''),
            ).entries()
        ) {
            breadcrumbItems[i].name = name
        }
    }
    // But if the two breadcrumbs are the same, I use the one from vtex
    if (breadcrumb.itemListElement.length === breadcrumbItems.length) {
        breadcrumbItems = breadcrumb.itemListElement
    }
    const params = URLi.searchParams
    const searchTitle = params?.get('q')
    const lastSegment = URLi.pathname.split('/').filter(Boolean).at(-1) ?? ''
    let title = searchTitle ?? lastSegment
        .replaceAll('-', ' ')
        .split(' ')
        .map(capitalize)
        .join(' ')
    // de 100 a 150 filter
    const priceMatch = [...title.matchAll(/De (\d*\.?\d+) A (\d*\.?\d+)/gi)]
    if (priceMatch.length) {
        const parentSegment = URLi.pathname.split('/').filter(Boolean).at(-2) ?? ''
        title = parentSegment
            .replaceAll('-', ' ')
            .split(' ')
            .map(capitalize)
            .join(' ')
    }
    params.delete('page')
    const selectedSortOptionLabel = params.get('O')
        ? portugueseMappings[params.get('O') as keyof typeof portugueseMappings] ??
            'Ordenar por:'
        : 'Ordenar por:'
    const _sortOptions = useMemo(() =>
        Object.entries(portugueseMappings)
            .filter(([key]) => sortOptions.some((option) => option.label === key))
            .map(([key, label]) => {
                const { value } = sortOptions.find(({ label }) => label === key) ?? {}
                if (!value) {
                    return {}
                }
                params.set('O', value)
                return {
                    key: value,
                    href: URLi.href,
                    label,
                }
            }), [sortOptions])
    const categoryName = breadcrumb?.itemListElement.at(-1)?.name ?? ''
    const categories = breadcrumb?.itemListElement?.reverse() ?? []
    // A mesma identificação de lista vai no view_item_list e no select_item
    // de cada card — sem isso o card caía no padrão `product_card`/`Produto`
    // do ProductCard e a seleção não casava com a lista vista (#281).
    const listView = {
        id: ga4?.item_list_id ?? 'search_result',
        name: ga4?.item_list_name ??
            stripHTML(title ?? '') ??
            'Departamento/Resultado de busca',
    }
    const viewItemListEvent = toAnalytics({
        type: 'view_item_list',
        data: {
            items: products.map((product, index) => toProductItem(product, { quantity: 1, index })),
            view: listView,
        },
    })
    const { records = 0, recordPerPage = 1, currentPage = 0, previousPage, nextPage } = pageInfo ??
        {}
    const pages = Math.ceil(records / (recordPerPage ?? records)) || 0
    let lastPage = 0
    for (let i = pages; i >= currentPage; i--) {
        const reached = i * recordPerPage - recordPerPage > MAX_FROM_PARAM_VTEX
        if (!reached) {
            lastPage = i
            break
        }
    }
    const previousUrl = new URL(url ?? '/', SITE.URL)
    const nextUrl = new URL(url ?? '/', SITE.URL)
    const firstUrl = new URL(url ?? '/', SITE.URL)
    const lastUrl = new URL(url ?? '/', SITE.URL)
    if (previousPage) {
        if (currentPage > 2) {
            previousUrl.searchParams.set('page', (currentPage - 1).toString())
        } else {
            previousUrl.searchParams.delete('page')
        }
    }
    if (nextPage && currentPage < lastPage) {
        nextUrl.searchParams.set('page', (currentPage + 1).toString())
        lastUrl.searchParams.set('page', lastPage.toString())
    }
    if (previousPage) {
        firstUrl.searchParams.delete('page')
    }
    return (
        <div className='flex flex-col w-full max-w-[1810px] mx-auto'>
            <div className='px-4 lg:px-5'>
                <div className='pt-2 pb-2 lg:pb-0 lg:pt-4'>
                    <Breadcrumb itemListElement={breadcrumbItems} />
                    <p className='lg:hidden text-[12px] py-1'>
                        Encontramos{' '}
                        <span className='text-red-wine font-bold'>
                            {pageInfo.records} produtos
                        </span>
                    </p>
                </div>

                {isMobile && matchedCategoryNav && (
                    <DepartmentCategoryNav
                        title={matchedCategoryNav.title}
                        items={matchedCategoryNav.items}
                    />
                )}

                {isMobile && (
                    <div className='grid grid-cols-2 gap-3 h-12'>
                        {filters.length > 0 && (
                            <FiltersDrawer
                                filters={filters}
                                categoryName={categoryName}
                                isSearchPage={isSearchPage}
                                url={url}
                                isMobile={isMobile}
                                isPerformanceTestPage={isPerformanceTestPage}
                                paginateFilterOnMobile={paginateFilterOnMobile}
                                brandPage={brandPage}
                            />
                        )}

                        <div className='relative bg-white text-black border border-[#eee] h-12'>
                            <input id='ordenar-por' type='checkbox' className='fixed hidden peer' />
                            <label
                                className='px-4 h-full w-full flex justify-between items-center text-sm cursor-pointer'
                                htmlFor='ordenar-por'
                            >
                                <span>{selectedSortOptionLabel}</span>
                                <Icon id='FilledChevronDown' size={7} />
                            </label>
                            <div className='absolute top-full left-0 overflow-y-auto transition-all max-h-0 bg-white border-neutral-200 w-full shadow-lg text-sm peer-checked:max-h-[400px] peer-checked:border z-[1]'>
                                <ul className='flex flex-col divide-y divide-solid divide-[#eee]'>
                                    {_sortOptions.map(({ key, label, href }) => (
                                        <li className='p-2'>
                                            <a key={key} href={href}>
                                                <span className='text-sm'>{label}</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
                <div
                    className={clx(
                        'grid grid-cols-1 mt-4 gap-x-[30px]',
                        !isMobile && 'grid-cols-[20%_1fr]',
                    )}
                >
                    {!isMobile && (
                        <aside>
                            <div className='mb-3 border-b border-[#ededed] h-[47px] flex items-center'>
                                <h2 className='text-2xl font-bold text-red-wine'>
                                    Filtre sua Busca
                                </h2>
                            </div>
                            <Filters
                                filters={filters}
                                categoryName={categoryName}
                                isSearchPage={isSearchPage}
                                url={url}
                                isMobile={isMobile}
                                isPerformanceTestPage={isPerformanceTestPage}
                                brandPage={brandPage}
                            />
                        </aside>
                    )}
                    <div className='space-y-4 lg:space-y-8'>
                        {!isMobile && matchedCategoryNav && (
                            <DepartmentCategoryNav
                                title={matchedCategoryNav.title}
                                items={matchedCategoryNav.items}
                            />
                        )}
                        <div className='bg-neutral-200 rounded pl-4 pr-[10px] h-[47px] flex items-center justify-center lg:justify-between'>
                            {!isMobile && (
                                <p className='hidden md:block'>
                                    Encontramos{' '}
                                    <span className='text-red-wine font-bold'>
                                        {pageInfo.records} produtos
                                    </span>
                                </p>
                            )}
                            <Pagination url={url} pageInfo={pageInfo} />
                            {_sortOptions.length > 0 && !isMobile && (
                                <div className='hidden md:block'>
                                    <div className='relative bg-white text-black border border-[#eee] w-[144px] px-2 cursor-pointer'>
                                        <input
                                            id='ordenar-por'
                                            type='checkbox'
                                            className='fixed hidden peer'
                                        />
                                        <label
                                            className='h-[27px] w-full flex justify-between items-center text-xs'
                                            htmlFor='ordenar-por'
                                        >
                                            <span>{selectedSortOptionLabel}</span>
                                            <Icon id='FilledChevronDown' size={7} />
                                        </label>
                                        <div className='absolute z-[2] top-full left-0 overflow-y-auto transition-all max-h-0 peer-checked:max-h-[400px] bg-white border border-neutral-200 w-full shadow-lg text-xs'>
                                            <ul className='flex flex-col py-3 tracking-tighter'>
                                                {_sortOptions.map(({ key, label, href }) => (
                                                    <li>
                                                        <a className='px-2' key={key} href={href}>
                                                            <span className='text-sm'>{label}</span>
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <div
                                id={productListId}
                                className='grid h-fit grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 items-center sm:gap-[30px] sm:px-4 md:px-[10px] md:gap-5 lg:px-[15px] lg:gap-8'
                            >
                                {products?.map((product, index) => {
                                    const shouldDisabled = disabledBuyMonthlySubscription &&
                                        product.productID === MONTHLY_SKU_ID

                                    return (
                                        <ProductCard
                                            key={`product-card-${product.productID}`}
                                            product={product}
                                            preload={false}
                                            isMobile={isMobile as boolean}
                                            index={index}
                                            itemListId={listView.id}
                                            itemListName={listView.name}
                                            isPerformanceTestPage={isPerformanceTestPage}
                                            disabledBuyMonthlySubscription={shouldDisabled}
                                        />
                                    )
                                })}
                            </div>
                            <div className='bg-neutral-200 mt-20 rounded flex items-center justify-center h-[47px]'>
                                <Pagination url={url} pageInfo={pageInfo} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* trigger="render": a grade é o conteúdo principal da página, então
                "a lista apareceu" = a grade existe — não depende do
                IntersectionObserver, que não roda em aba de fundo/ocluída. */}
            {products.length > 0 && (
                <SendEventOnView
                    id={productListId}
                    event={viewItemListEvent}
                    trigger='render'
                />
            )}
            {!isSearchPage && (
                <SendCustomEventOnView
                    event={[
                        {
                            label: 'pageCategory',
                            value: 'Departament',
                        },
                        {
                            label: 'departmentName',
                            value: categories.at(-1)?.name ?? '',
                        },
                        {
                            label: 'categoryName',
                            value: categories.at(-2)?.name ?? '',
                        },
                    ]}
                />
            )}
            {isSearchPage && (
                <SendCustomEventOnView
                    event={[
                        {
                            label: 'pageCategory',
                            value: 'InternalSiteSearch',
                        },
                        {
                            label: 'siteSearchTerm',
                            value: title,
                        },
                    ]}
                />
            )}
        </div>
    )
}

export function LoadingFallback() {
    return (
        <div className='w-full'>
            <div className='px-4 lg:px-5 '>
                <div className='pt-2 pb-2 lg:pb-0 lg:pt-4 w-full h-[58px] lg:h-8'>
                    <div className='bg-gray-200 w-full h-full'></div>
                </div>
                <div className='lg:grid grid-cols-5 w-full mt-4 lg:gap-x-[30px] lg:grid-cols-[20%_1fr]'>
                    <div className='hidden lg:block'>
                        <div className='mb-3 h-[47px]'>
                            <div className='h-full w-full bg-gray-300'></div>
                        </div>
                        <div className='animate-pulse flex flex-col gap-2 w-full'>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className='h-[258px] bg-gray-300 w-full'></div>
                            ))}
                        </div>
                    </div>
                    <div className='animate-pulse w-full px-4'>
                        <div className='grid grid-cols-2 gap-3 h-12 lg:hidden'>
                            <div className='bg-gray-200' />
                            <div className='bg-gray-200' />
                        </div>
                        <div className='bg-neutral-200 rounded pl-4 pr-[10px] h-[47px] flex items-center justify-center lg:justify-between mt-4 lg:mt-0'>
                            <div className='ml-4 h-2.5 bg-gray-200 rounded-full w-48'></div>
                        </div>
                        <div className='grid h-fit grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 items-center sm:gap-[30px] sm:px-4 md:px-[10px] md:gap-5 lg:px-[15px] lg:gap-8 mt-4'>
                            {Array.from({ length: 16 }).map((_, i) => <ProductCardLoadingFallback key={i} />)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
